if (!customElements.get('auren-stories')) {
  customElements.define('auren-stories', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('[data-stories-track]');
      this.cards = [...this.querySelectorAll('[data-story]')];
      if (!this.track || !this.cards.length) return;
      const previous = this.querySelector('[data-stories-prev]');
      const next = this.querySelector('[data-stories-next]');
      const error = this.querySelector('[data-stories-error]');
      const move = delta => {
        const index = Math.max(0, Math.min(this.cards.length - 1, this.index + delta));
        this.track.scrollTo({ left: this.cards[index].offsetLeft - this.cards[0].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      this.update = () => {
        const left = this.track.getBoundingClientRect().left;
        this.index = this.cards.reduce((best, card, i) => Math.abs(card.getBoundingClientRect().left - left) < Math.abs(this.cards[best].getBoundingClientRect().left - left) ? i : best, 0);
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.querySelector('[data-stories-nav]').hidden = max < 2;
        previous.disabled = this.track.scrollLeft < 2;
        next.disabled = this.track.scrollLeft >= max - 2;
        this.querySelector('[data-stories-count]').textContent = `${this.index + 1} / ${this.cards.length}`;
      };
      previous.addEventListener('click', () => move(-1), { signal });
      next.addEventListener('click', () => move(1), { signal });
      this.track.addEventListener('keydown', event => {
        if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      }, { signal });
      this.track.addEventListener('scroll', () => { cancelAnimationFrame(this.frame); this.frame = requestAnimationFrame(this.update); }, { signal });
      this.cards.forEach(card => {
        const video = card.querySelector('video');
        const play = card.querySelector('[data-story-play]');
        if (!video || !play) return;
        const playLabel = play.getAttribute('aria-label');
        const mutedByMerchant = card.dataset.storySoundEnabled !== 'true';
        const keepSoundSetting = () => {
          video.controls = false;
          if (video.defaultMuted !== mutedByMerchant) video.defaultMuted = mutedByMerchant;
          if (video.muted !== mutedByMerchant) video.muted = mutedByMerchant;
        };
        const updatePlayback = () => {
          const playing = !video.paused && !video.ended;
          card.classList.toggle('is-playing', playing);
          play.hidden = false;
          play.setAttribute('aria-label', playing ? play.dataset.pauseLabel : playLabel);
        };
        keepSoundSetting();
        video.disablePictureInPicture = true;
        video.disableRemotePlayback = true;
        video.addEventListener('volumechange', keepSoundSetting, { signal });
        video.addEventListener('loadedmetadata', keepSoundSetting, { signal });
        updatePlayback();
        play.addEventListener('click', async () => {
          error.hidden = true;
          keepSoundSetting();
          if (!video.paused && !video.ended) { video.pause(); return; }
          try { await video.play(); } catch (_) { error.hidden = false; updatePlayback(); }
        }, { signal });
        video.addEventListener('play', () => {
          keepSoundSetting();
          document.querySelectorAll('auren-stories video').forEach(other => { if (other !== video) other.pause(); });
          updatePlayback();
        }, { signal });
        video.addEventListener('pause', updatePlayback, { signal });
        video.addEventListener('ended', updatePlayback, { signal });
      });
      this.visibility = new IntersectionObserver(entries => {
        entries.forEach(entry => { if (!entry.isIntersecting) entry.target.querySelector('video')?.pause(); });
      }, { threshold: 0.05 });
      this.cards.forEach(card => this.visibility.observe(card));
      this.resize = new ResizeObserver(this.update);
      this.resize.observe(this.track);
      this.update();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.visibility?.disconnect(); this.resize?.disconnect(); cancelAnimationFrame(this.frame);
      this.querySelectorAll('video').forEach(video => video.pause());
    }
  });
}

if (!customElements.get('auren-cart-rewards')) {
  customElements.define('auren-cart-rewards', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const refresh = () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.refresh(), 120); };
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, refresh);
      document.addEventListener('auren:cart-updated', refresh, { signal: this.controller.signal });
      window.addEventListener('pageshow', event => { if (event.persisted) refresh(); }, { signal: this.controller.signal });
    }
    async refresh() {
      this.request?.abort();
      this.request = new AbortController();
      const currentRequest = this.request;
      try {
        // Server-rendered money and translations stay consistent with Shopify Markets.
        const url = new URL(window.location.href);
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', this.dataset.section);
        const response = await fetch(url, { signal: currentRequest.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        const data = await response.json();
        const html = data[this.dataset.section];
        if (typeof html !== 'string' || !this.isConnected || currentRequest.signal.aborted) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = [...parsed.querySelectorAll('auren-cart-rewards')].find(el => el.dataset.block === this.dataset.block);
        this.hidden = !fresh;
        if (fresh) this.innerHTML = fresh.innerHTML;
      } catch (error) {
        // Keep the last confirmed cart value on a transient network failure.
        if (error.name !== 'AbortError') this.dataset.refreshPending = 'true';
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.request?.abort(); this.unsubscribe?.(); clearTimeout(this.timer);
    }
  });
}
